import { useState, useCallback } from "react";
import { RoleManagementService } from "../services/roleManagement";
import { User, UserRole } from "../../../shared/types";

export type SortField = "name" | "email" | "role" | "createdAt";
export type SortDirection = "asc" | "desc";

export function useRoleManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchUsers = useCallback(
    async (page?: number, limit?: number) => {
      try {
        setLoading(true);
        setError(null);
        const response = await RoleManagementService.getAllUsers(
          page,
          limit,
          sortField,
          sortDirection
        );
        setUsers(response.users);
        setPagination({
          page: response.page,
          totalPages: response.totalPages,
          totalCount: response.totalCount,
          hasNextPage: response.hasNextPage,
          hasPrevPage: response.hasPrevPage,
        });
      } catch (err) {
        console.error("Failed to fetch users:", err);
        setError("Failed to fetch users");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [sortField, sortDirection]
  );

  const updateUserRole = useCallback(async (userId: string, role: UserRole) => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = await RoleManagementService.updateUserRole(
        userId,
        role
      );

      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.id === userId ? updatedUser : user))
      );

      return updatedUser;
    } catch (err) {
      console.error("Failed to update user role:", err);
      setError("Failed to update user role");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUsersByRole = useCallback(async (role: UserRole) => {
    try {
      setLoading(true);
      setError(null);
      const roleUsers = await RoleManagementService.getUsersByRole(role);
      return roleUsers;
    } catch (err) {
      console.error("Failed to fetch users by role:", err);
      setError("Failed to fetch users by role");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const setSort = useCallback(
    (field: SortField, direction?: SortDirection) => {
      if (direction) {
        // If direction is explicitly provided, use it
        setSortField(field);
        setSortDirection(direction);
      } else {
        // If no direction provided, toggle based on current state
        const newDirection =
          field === sortField && sortDirection === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortDirection(newDirection);
      }
    },
    [sortField, sortDirection]
  );

  return {
    users,
    loading,
    error,
    pagination,
    sortField,
    sortDirection,
    fetchUsers,
    updateUserRole,
    getUsersByRole,
    setSort,
  };
}
